import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, LayoutDashboard, Settings } from 'lucide-react';
import { Button } from './ui/button';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../context/LanguageContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check for logged in user
  useEffect(() => {
    const checkUser = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        setUser(JSON.parse(userData));
      } else {
        setUser(null);
      }
    };
    
    checkUser();
    // Listen for storage changes (login/logout in other tabs)
    window.addEventListener('storage', checkUser);
    
    // Check on route change
    return () => window.removeEventListener('storage', checkUser);
  }, [location]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  const navItems = [
    { name: 'Submit Prayer', path: '/prayer-request' },
    { name: 'Holy Sites', path: '/sacred-sites' },
    { name: 'Prayer Wall', path: '/prayer-wall' },
    { name: 'Testimonies', path: '/testimonials' },
    { name: 'Scripture', path: '/holy-word' },
    { name: 'About Us', path: '/about' },
    { name: 'Verify Prayer', path: '/verify' }
  ];

  // Check if we're on the home page
  const isHomePage = location.pathname === '/';

  // Determine if header should be solid (scrolled OR not on homepage)
  const shouldBeSolid = isScrolled || !isHomePage;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      shouldBeSolid ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-4 group">
            <div className={`rounded-full transition-all duration-300 overflow-hidden ${
              shouldBeSolid 
                ? 'bg-white shadow-lg ring-2 ring-amber-200' 
                : 'bg-white/95 backdrop-blur-md border-2 border-white shadow-xl'
            }`}>
              <img 
                src="/logo-circle.png"
                alt="Jerusalem Holy Prayer"
                className="h-10 w-10 sm:h-16 sm:w-16 object-cover transform transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
              />
            </div>
            <div className="flex flex-col">
              <span className={`font-serif text-lg sm:text-2xl font-bold tracking-wide leading-tight transition-colors ${
                shouldBeSolid ? 'text-amber-700' : 'text-white drop-shadow-lg'
              }`}>Jerusalem</span>
              <span className={`text-xs sm:text-sm font-semibold tracking-widest uppercase leading-tight transition-colors ${
                shouldBeSolid ? 'text-amber-600/70' : 'text-amber-300 drop-shadow-md'
              }`}>Holy Prayer</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-10">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-base font-semibold transition-colors hover:text-amber-500 ${
                  location.pathname === item.path
                    ? 'text-amber-500'
                    : shouldBeSolid
                    ? 'text-gray-700'
                    : 'text-white'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Login/User Button */}
          <div className="hidden lg:flex items-center space-x-4">
            <LanguageSelector />
            {user ? (
              <div className="flex items-center space-x-3">
                {user.is_admin && (
                  <Link to="/admin">
                    <Button
                      variant="outline"
                      className={`transition-all ${
                        shouldBeSolid
                          ? 'bg-amber-600 border-amber-600 text-white hover:bg-amber-700'
                          : 'bg-amber-600 border-amber-600 text-white hover:bg-amber-700'
                      }`}
                      data-testid="admin-btn"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      {t('nav.admin')}
                    </Button>
                  </Link>
                )}
                <Link to="/dashboard">
                  <Button
                    variant="outline"
                    className={`transition-all ${
                      shouldBeSolid
                        ? 'bg-white border-amber-600 text-amber-600 hover:bg-amber-50'
                        : 'bg-white/10 border-white text-white hover:bg-white/20 backdrop-blur-sm'
                    }`}
                    data-testid="dashboard-btn"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    {t('nav.dashboard')}
                  </Button>
                </Link>
                <button
                  onClick={handleLogout}
                  className={`p-2 rounded-lg transition-colors ${
                    shouldBeSolid 
                      ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' 
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                  title="Sign out"
                  data-testid="header-logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <Link to="/login">
                <Button
                  variant="outline"
                  className={`transition-all ${
                    shouldBeSolid
                      ? 'bg-white border-amber-600 text-amber-600 hover:bg-amber-50'
                      : 'bg-white/10 border-white text-white hover:bg-white/20 backdrop-blur-sm'
                  }`}
                  data-testid="login-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  {t('nav.login')}
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            <LanguageSelector />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className={shouldBeSolid ? 'text-gray-900' : 'text-white'} size={24} />
              ) : (
                <Menu className={shouldBeSolid ? 'text-gray-900' : 'text-white'} size={24} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="container mx-auto px-4 py-4 space-y-3">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg text-base font-semibold transition-colors ${
                  location.pathname === item.path
                    ? 'bg-amber-50 text-amber-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
            {user ? (
              <>
                {user.is_admin && (
                  <Link 
                    to="/admin" 
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block"
                  >
                    <Button className="w-full bg-amber-700 hover:bg-amber-800 text-white">
                      <Settings className="w-4 h-4 mr-2" />
                      {t('mobileMenu.adminPanel')}
                    </Button>
                  </Link>
                )}
                <Link 
                  to="/dashboard" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block"
                >
                  <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    {t('nav.dashboard')}
                  </Button>
                </Link>
                <Button 
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  variant="outline" 
                  className="w-full border-gray-300"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('mobileMenu.signOut')}
                </Button>
              </>
            ) : (
              <Link 
                to="/login" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block"
              >
                <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white">
                  <User className="w-4 h-4 mr-2" />
                  {t('nav.login')}
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;